import { useState } from "react";
//imports
import { default as SelectComponent, StylesConfig } from "react-select";
import _ from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid, regular } from "@fortawesome/fontawesome-svg-core/import.macro"
import FileUploadComponent from "../../../../../components/fileUpload/fileUpload";
import MiniModalController from "../../../../../components/modals/miniModal/miniModalController";
import ChatComponentController from "../../../../../components/chatComponent/chatComponentController";
import FullSizeModalController from "../../../../../components/modals/fullSizeModal/fullSizeModalController";
import Constants, { TIME_STATUS, COLOR_STATUS, ENROLLMENT_STATUS, ROLE, PERMISSIONS_CODES, ATTENDANCE_STATUS, ATTENDANCE_TYPE } from "../../../../../constants";
import noInfo from "../../../../../assets/images/noInfo.gif"
import { AttendancesModalViewProps, ColourOption } from "./interfaces";
import ExtraStudentModalCrontoller from "../extraStudentModal/extraStudentModalCrontoller";
import { Button, Tab, Tabs, OverlayTrigger, Tooltip } from "react-bootstrap";

import { cursoCorto } from "../../../../../utils/cursoCorto";
import PaseDeLista from "../paseDeLista/paseDeLista";
import { useEsTelefono } from "../../../../../utils/esTelefono";
import { horarioBonito } from "../../../../../utils/horarioBonito";

//styles
import { styles } from './styles';
import "./styles.css";
import "./styles.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../globals";

/*
    Orden de los alumnos, el mismo en el pase de lista del dia y en el historico
    (Esteban, 2026-09-18):

        Activos -> Graduados -> Retirados -> No comenzado -> lo demas

    y dentro de cada grupo, por nombre.

    Antes solo se separaba "activo / no activo" y todo lo que no estaba activo
    quedaba en el orden que devolviera la base, que no tiene ninguno: un retirado
    podia salir antes que un graduado, y al dia siguiente al reves. El sort_order
    del catalogo tampoco sirve tal cual, porque ahi Especialidad va antes que
    Retirado y Cambio de horario antes que No comenzado.

    Los estados que no estan en la lista de arriba van todos al final, ordenados
    entre si por el sort_order del catalogo para que por lo menos sea estable.
*/
const PRIORIDAD_DE_ESTADO : {[estado : number] : number} =
{
    [ENROLLMENT_STATUS.ACTIVE]      : 1,
    [ENROLLMENT_STATUS.COMPLETED]   : 2,   //"Graduado"
    [ENROLLMENT_STATUS.RETIRED]     : 3,
    [ENROLLMENT_STATUS.NOT_STARTED] : 4
};

const prioridadDeMatricula = (fila : any) : number =>
    PRIORIDAD_DE_ESTADO[fila?.enrollmentStatusId]
        ?? (100 + Number(fila?.enrollmentStatusSortOrder ?? 0));

/*
    Las matriculas en "Error" no son alumnos, son errores de captura: no se
    listan nunca. El servidor ya las excluye de las dos consultas, pero se vuelve
    a filtrar aqui para que ninguna pantalla dependa de que se acuerde.
*/
const porEstadoDeMatricula = (lista : any[], nombreDe : (fila : any) => string) : any[] =>
    [...(lista || [])]
        .filter((fila : any) => fila?.enrollmentStatusId !== ENROLLMENT_STATUS.ERROR)
        .sort((a : any, b : any) =>
        {
            const porEstado = prioridadDeMatricula(a) - prioridadDeMatricula(b);

            if (porEstado !== 0) return porEstado;

            return nombreDe(a).localeCompare(nombreDe(b), "es");
        });

const AttendancesModalView = ({
    deleteSessions,
    createSessions,
    selectedSchedule,
    selectedAttendance,
    editableAttendance,
    baseEditableAttendance,
    selectedSession,
    idleLocked,
    checkPoint,
    toggleStudentAttendance,
    showModal,
    hideModal,
    sessionsCount,
    changeSession,
    sessionOptions,
    userProfile,
    fileChange,
    uploadDocuments,
    downloadDocuments,
    allowedFiletypes,
    downloadFile,
    selectedNote,
    showNoteModal,
    isShowNoteModal,
    closeNoteModal,
    changeNote,
    isNewAttendance,
    baseExtraStudentsList,
    extraStudentsList,
    setExtraStudentsList,
    openExtraStudentModal,
    showExtraStudentModal,
    closeExtraStudentModal,
    hasChanges,
    addClasses,
    historicAttendances,
    sessions,
    isLecture,
    showNoteModalLecture,
    activeTab,
    selectTab,
    showExtraStudentModalLecture,
    closeExtraStudentModalLecture,
    openExtraStudentModalLecture,
    extraStudentsListLecture,
    initialStudentsCount,
    currentStudentsCount,
    addDayProps,
    editDayProps,
    detailDayProps,
    cancellationOptionsProps,
    cancellationConfirmProps,
    permissions
}: AttendancesModalViewProps) => {


    /*
        El mensaje decía "30 minutos", pero Util::timeStatus del backend siempre ha
        usado 60. Los profesores llevaban tiempo leyendo un plazo más corto que el
        que de verdad tenían.
    */
    /*
        Aviso de Caja: quien trae mensualidad vencida. No impide nada —el profesor
        pasa lista igual, como siempre—, solo avisa una vez y se quita con un clic.
        El de kits queda apagado hasta nuevo aviso. (Esteban, 2026-09-16)
    */
    const [avisoCajaCerrado, setAvisoCajaCerrado] = useState<boolean>(false);
    const esTelefono = useEsTelefono();

    /*
        El historico ponia una columna por cada sesion del horario. En un diplomado
        de cuatro meses son treinta y pico y habia que irse de lado para leerlo.
        Ahora se muestra una ventana de las ultimas sesiones y el resto queda
        plegado a un clic. (Esteban, 2026-09-16)
    */
    const [ventanaSesiones, setVentanaSesiones] = useState<number>(4);
    /*
        En telefono una matriz de alumnos por fechas no funciona: hay que arrastrar
        de lado para leer un nombre. Se parte en las dos preguntas que la gente trae
        de verdad: "quien vino tal dia" y "como va este alumno".
        (Esteban, 2026-09-16)
    */
    const [modoHistorico, setModoHistorico] = useState<"dia" | "alumno">("dia");
    const [diaElegido, setDiaElegido] = useState<number | null>(null);
    const [alumnoElegido, setAlumnoElegido] = useState<number | null>(null);

    /*
        La lista venia en el orden que la manda el servidor y las matriculas que ya
        no corren —"No comenzado", "Cambio de horario"— salian revueltas, a veces
        de primeras. El orden completo vive en porEstadoDeMatricula, arriba.
        (Esteban, 2026-09-16; ampliado 2026-09-18)
    */
    const historicoOrdenado = porEstadoDeMatricula(
        historicAttendances,
        (fila: any) => String(fila.fullName || "")
    );

    const asistenciaOrdenada = porEstadoDeMatricula(
        selectedAttendance || [],
        (fila: any) => String(fila.firstName || "") + " " + String(fila.lastName || "")
    );

    const totalSesiones = (sessions || []).length;
    const desdeSesion = ventanaSesiones >= totalSesiones
        ? 0
        : Math.max(0, totalSesiones - ventanaSesiones);
    const sesionesVisibles = (sessions || []).slice(desdeSesion);
    const sesionesOcultas = desdeSesion;

    /*
        El aviso se sacaba de TODAS las matriculas y la lista de abajo solo muestra
        las activas, asi que salian nombres en el aviso que no estaban en la lista.
        Se cuentan los mismos. (Esteban, 2026-09-16)
    */
    /*
        "Activos hoy" salia en 0 porque se leia de una bandera del horario
        (activeEnrollments) que no siempre viene con dato. Se cuenta sobre las
        mismas matriculas que se estan mostrando abajo, que es lo que la palabra
        significa y ademas nunca se puede contradecir con la lista.
        (Esteban, 2026-09-16)
    */
    const activosAhora: number = (selectedAttendance || []).filter
        (
            (matricula: any) => matricula?.enrollmentStatusId === ENROLLMENT_STATUS.ACTIVE
        ).length;

    const deudores: any[] = (selectedAttendance || []).filter
        (
            (matricula: any) =>
                matricula?.enrollmentStatusId === ENROLLMENT_STATUS.ACTIVE &&
                matricula?.generalBalance?.expiredMonthlyBalance?.value > 0
        );

    const timeStatusMessages: { [key: string]: string } = {
        "1": `La sesión aún no ha comenzado, vuelve a las ${selectedSession ? selectedSession.sessionHour : ''}`,
        "2": `${selectedSession ? selectedSession.sessionOn.split("/")[0] : ''} de ${selectedSession ? selectedSession.sessionOnMonth : ''}`,
        "3": 'La sesión tiene más de una hora de iniciada, contacte a Servicios Académicos para registrar asistencias.',
        "4": 'La sesión de hoy ya terminó'
    }

    const dot = (color = 'transparent') => ({
        alignItems: 'center',
        display: 'flex',
        ':before': {
            backgroundColor: color,
            borderRadius: 10,
            content: '" "',
            display: 'block',
            marginRight: 8,
            height: 10,
            width: 10,
        },
    });

    const colourStyles: StylesConfig<ColourOption> = {
        control: (baseStyles, state) => ({
            ...baseStyles,
            ...styles.modal.header.select.component.v,
            width: "100%",
            borderColor: selectedSession ? selectedSession.color : "gray",
            borderWidth: "1px"
        }),
        option: (styles, { data, isDisabled, isFocused, isSelected }) => {
            const color = data.color;
            return {
                ...styles,
                backgroundColor: (
                    isSelected
                        ?
                        color
                        :
                        (
                            isFocused
                                ?
                                color + '11'
                                :
                                undefined
                        )
                ),
                color: (
                    isSelected
                        ?
                        'white'
                        :
                        color
                )
            };
        },
        input: (styles) => ({ ...styles, ...dot() }),
        placeholder: (styles) => ({ ...styles, ...dot('#ccc') }),
        singleValue: (styles, { data }) => ({ ...styles, ...dot(data.color) }),
        container: (styles) => ({ ...styles, zIndex: 10 })
    };

    return (
        <>
            <FullSizeModalController
                title={
                    <div>
                        <FontAwesomeIcon style={{ color: "rgb(143, 145, 218)", fontSize: "14px", marginRight: "10px" }} icon={solid("list-check")} />
                        <span style={{ color: "var(--wa-apagado)", fontSize: "14px" }}>
                            Asistencias
                        </span>
                    </div>
                }
                show={showModal}
                onClose={() => {
                    checkPoint(() => hideModal())
                }}
                headerContent={
                    <div className="row">
                        <div className="w-100">
                            <div className="d-flex flex-wrap">
                                <div className="w-100">
                                    <span className="d-flex justify-content-center modal_header_title_v"
                                        style={styles.modal.header.title.v}>
                                        {cursoCorto(selectedSchedule.course)}
                                    </span>
                                    <div className="d-flex flex-row justify-content-center flex-wrap align-items-center">
                                        <div style={styles.modal.header.info.v}>
                                            <div style={styles.modal.header.info.course.v}>
                                                <div className="modal_header_info_card_span_v" style={styles.modal.header.info.card.v}>
                                                    <span className="modal_header_info_card_span_v" style={styles.modal.header.info.card.span.v}>
                                                        Periodo:
                                                    </span>
                                                    {selectedSchedule.period}
                                                </div>
                                                <div className="modal_header_info_card_span_v" style={styles.modal.header.info.card.v}>
                                                    <span className="modal_header_info_card_span_v" style={styles.modal.header.info.card.span.v}>
                                                        Horario:
                                                    </span>
                                                    {horarioBonito(selectedSchedule.name)}
                                                </div>
                                                <div className="modal_header_info_card_span_v" style={styles.modal.header.info.card.v}>
                                                    <span className="modal_header_info_card_span_v" style={styles.modal.header.info.card.span.v}>
                                                        Aula:
                                                    </span>
                                                    {`${selectedSchedule.building} ${selectedSchedule.classroom}`}
                                                </div>
                                                <div className="modal_header_info_card_span_v" style={styles.modal.header.info.card.v}>
                                                    <span className="modal_header_info_card_span_v" style={styles.modal.header.info.card.span.v}>
                                                        Módulo:
                                                    </span>
                                                    {selectedSchedule.level}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="modal_header_info_card_span_v" style={styles.modal.header.info.card.v}>
                                                    <span className="modal_header_info_card_span_v blueDay">
                                                        Inscritos el primer día:
                                                    </span>
                                                    {initialStudentsCount}
                                                </div>
                                                <div className="modal_header_info_card_span_v" style={styles.modal.header.info.card.v}>
                                                    <span className="modal_header_info_card_span_v greenSuccess">
                                                        Activos hoy:
                                                    </span>
                                                    {selectedAttendance ? activosAhora : currentStudentsCount}
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            className="d-flex flex-column align-items-center select-component"
                                            style={styles.modal.header.select.v}>
                                            <span className="modal_header_info_card_span_v" style={styles.modal.header.select.title.v}>
                                                Asistencia Disponible
                                            </span>
                                            {
                                                selectedSession
                                                    ?
                                                    (
                                                        userProfile.currentRole === ROLE.MENTOR
                                                            ?
                                                            (
                                                                selectedSession.isOnCurrentDay
                                                                    ?
                                                                    <span className="text-center">
                                                                        {timeStatusMessages[selectedSession.timeStatusId]}
                                                                    </span>
                                                                    :
                                                                    <span className="text-center">Hoy no hay sesión</span>
                                                            )
                                                            :
                                                            <div className="d-flex flex-column">
                                                                <SelectComponent
                                                                    options={sessionOptions}
                                                                    onChange={(value: any) => {
                                                                        checkPoint(() => changeSession(value))
                                                                    }}
                                                                    styles={colourStyles}
                                                                    classNames={{
                                                                        control: (state) => ("styles_modal_select_control_v"),
                                                                        dropdownIndicator: (state) => ("styles_modal_select_dropdownIndicator_v"),
                                                                        option: (state) => ("styles_modal_select_option_v"),
                                                                        input: (state) => ("styles_modal_select_input_v"),
                                                                        singleValue: (state) => ("styles_modal_select_single_v")
                                                                    }}
                                                                    isDisabled={idleLocked}
                                                                    value={selectedSession}
                                                                />
                                                                <span className="modal_header_info_card_span_v text-center w-100" style={{ color: selectedSession.color }}>
                                                                    {
                                                                        selectedSession.color == COLOR_STATUS.FINALIZED ?
                                                                            "Asistencia registrada"
                                                                            :
                                                                            (
                                                                                selectedSession.color == COLOR_STATUS.AVAILABLE ?
                                                                                    "Asistencia por Registrar"
                                                                                    :
                                                                                    "No se registro Asistencia"
                                                                            )
                                                                    }
                                                                </span>
                                                            </div>
                                                    )
                                                    :
                                                    <span className="text-center">Ninguno</span>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                }
                bodyContent={
                    <Tabs
                        activeKey={activeTab}
                        id="attendances-tabs"
                        className="mb-3"
                        onSelect={selectTab}
                    >
                        <Tab className="attendance-tab" eventKey="attendances" title="Asistencias">
                            {
                                (
                                    !selectedSession
                                    || !selectedAttendance
                                    || (userProfile.currentRole === ROLE.MENTOR && !selectedSession.isOnCurrentDay)
                                    || (userProfile.currentRole === ROLE.MENTOR && selectedSession.timeStatusId != TIME_STATUS.ON_TIME)
                                ) ?
                                    <div className="d-flex flex-column align-items-center mt-4">
                                        {
                                            idleLocked
                                                ?
                                                <span>Cargando...</span>
                                                :
                                                <div className="waVacioModal">
                                                    <h4>
                                                        {
                                                            !selectedSession
                                                                ? "Este horario todavía no tiene sesiones"
                                                                : (userProfile.currentRole === ROLE.MENTOR && !selectedSession.isOnCurrentDay
                                                                    ? "Hoy no hay clase de este horario"
                                                                    : "Todavía no se puede registrar")
                                                        }
                                                    </h4>
                                                    <p>
                                                        {
                                                            !selectedSession
                                                                ? "Servicios Académicos tiene que crear los días de clase antes de poder pasar lista."
                                                                : (selectedSession && timeStatusMessages[selectedSession.timeStatusId]
                                                                    ? timeStatusMessages[selectedSession.timeStatusId]
                                                                    : "Elige otra sesión en la lista de arriba.")
                                                        }
                                                    </p>
                                                </div>
                                        }
                                    </div>
                                    :
                                    esTelefono ?
                                    <>
                                        {
                                            deudores.length === 0 || avisoCajaCerrado ? null :
                                                <div className="waAvisoCaja">
                                                    <div className="waAvisoCajaTexto">
                                                        <h5>
                                                            {deudores.length === 1
                                                                ? "1 alumno debe pasar a Caja"
                                                                : deudores.length + " alumnos deben pasar a Caja"}
                                                        </h5>
                                                        <ul>
                                                            {
                                                                deudores.map((matricula: any) =>
                                                                    <li key={"DM_" + matricula.enrollmentId}>
                                                                        {matricula.firstName} {matricula.lastName}
                                                                    </li>
                                                                )
                                                            }
                                                        </ul>
                                                    </div>
                                                    <button type="button" onClick={() => setAvisoCajaCerrado(true)}>
                                                        Entendido
                                                    </button>
                                                </div>
                                        }
                                        <PaseDeLista
                                            selectedSchedule={selectedSchedule}
                                            selectedSession={selectedSession}
                                            selectedAttendance={asistenciaOrdenada}
                                            editableAttendance={editableAttendance}
                                            toggleStudentAttendance={toggleStudentAttendance}
                                            showNoteModal={showNoteModal}
                                            idleLocked={idleLocked}
                                            isNewAttendance={isNewAttendance}
                                            uploadDocuments={uploadDocuments}
                                            fileChange={fileChange}
                                            onGuardar={() => checkPoint()}
                                            puedeGuardar={hasChanges(isNewAttendance).status}
                                        />
                                    </>
                                    :
                                    <div>
                                        {
                                            deudores.length === 0 || avisoCajaCerrado ? null :
                                                <div className="waAvisoCaja">
                                                    <div className="waAvisoCajaTexto">
                                                        <h5>
                                                            {deudores.length === 1
                                                                ? "1 alumno debe pasar a Caja"
                                                                : deudores.length + " alumnos deben pasar a Caja"}
                                                        </h5>
                                                        <ul>
                                                            {
                                                                deudores.map((matricula: any) =>
                                                                    <li key={"D_" + matricula.enrollmentId}>
                                                                        {matricula.firstName} {matricula.lastName}
                                                                    </li>
                                                                )
                                                            }
                                                        </ul>
                                                    </div>
                                                    <button type="button" onClick={() => setAvisoCajaCerrado(true)}>
                                                        Entendido
                                                    </button>
                                                </div>
                                        }
                                        <table className={`${userProfile.currentRole === ROLE.MENTOR ? "menotrStylesMobile" : ""} specialTable attendanceTable`}>
                                            <thead>
                                                <tr>
                                                    <th className="text-center" style={{ color: selectedSession.color }}>
                                                        {selectedSession.sessionOn}
                                                    </th>
                                                    <th>
                                                        Nombre
                                                    </th>
                                                    <th>
                                                        Estatus Matrícula
                                                    </th>
                                                    <th className="menotrStylesMobileItem">
                                                        Correlativo
                                                    </th>
                                                    <th className="menotrStylesMobileItem">
                                                        Última Actualización
                                                    </th>
                                                    <th className="text-center menotrStylesMobileItem">
                                                        Total a Fecha
                                                    </th>
                                                    <th className="text-center menotrStylesMobileItem">
                                                        %
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {
                                                    asistenciaOrdenada.map((enrollment) =>
                                                        <tr key={"S_" + enrollment.enrollmentId}
                                                            className={addClasses("enrollment", enrollment)}>
                                                            <td className="text-center">
                                                                {
                                                                    (
                                                                        enrollment.enrollmentStatusId == ENROLLMENT_STATUS.ACTIVE //active
                                                                            ?
                                                                            <div className="d-flex flex-column">
                                                                                {/*
                                                                                    Era una casilla con la palabra en 12px debajo: habia que
                                                                                    fijarse en el color del cuadrito para saber que decia. Ahora
                                                                                    son los dos estados a la vista y se toca el que toca, igual
                                                                                    que en telefono. (Esteban, 2026-09-16)
                                                                                */}
                                                                                <div className="waPar">
                                                                                    <button
                                                                                        type="button"
                                                                                        className={editableAttendance[enrollment.enrollmentId].isPresent ? "waSi activo" : "waSi"}
                                                                                        aria-pressed={editableAttendance[enrollment.enrollmentId].isPresent}
                                                                                        disabled={idleLocked}
                                                                                        onClick={() => {
                                                                                            if (!editableAttendance[enrollment.enrollmentId].isPresent) {
                                                                                                toggleStudentAttendance(enrollment.enrollmentId)
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        Presente
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        className={editableAttendance[enrollment.enrollmentId].isPresent ? "waNo" : "waNo activo"}
                                                                                        aria-pressed={!editableAttendance[enrollment.enrollmentId].isPresent}
                                                                                        disabled={idleLocked}
                                                                                        onClick={() => {
                                                                                            if (editableAttendance[enrollment.enrollmentId].isPresent) {
                                                                                                toggleStudentAttendance(enrollment.enrollmentId)
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        Ausente
                                                                                    </button>
                                                                                </div>
                                                                                {
                                                                                    /* La nota estaba escondida para el profesor. Es quien ve
                                                                                       llegar tarde o salir enfermo a alguien. (Esteban, 2026-09-16) */
                                                                                    false
                                                                                        ?
                                                                                        ''
                                                                                        :
                                                                                        <button
                                                                                            onClick={() => { showNoteModal(editableAttendance[enrollment.enrollmentId]) }}
                                                                                            disabled={idleLocked}
                                                                                            style={{
                                                                                                ...styles.modal.body.addNote.v,
                                                                                                color: editableAttendance[enrollment.enrollmentId].attendanceNotes ? "var(--wa-acento)" : ""
                                                                                            }}
                                                                                        >
                                                                                            <span style={styles.modal.body.addNote.span.v}>
                                                                                                {`Nota ${editableAttendance[enrollment.enrollmentId].attendanceNotes ? '' : '+'}`}
                                                                                            </span>
                                                                                        </button>
                                                                                }
                                                                            </div>
                                                                            :
                                                                            <button className="btn btn-default" style={styles.modal.body.noActive.v}>
                                                                                <FontAwesomeIcon
                                                                                    size="xl"
                                                                                    icon={
                                                                                        editableAttendance[enrollment.enrollmentId].isPresent
                                                                                            ?
                                                                                            regular("square-check")
                                                                                            :
                                                                                            solid("square")
                                                                                    } style={{
                                                                                        ...styles.modal.body.noActive.icon.v,
                                                                                        color: editableAttendance[enrollment.enrollmentId].isPresent
                                                                                            ?
                                                                                            "#00e66980"
                                                                                            :
                                                                                            styles.modal.body.noActive.icon.v.color
                                                                                    }} />
                                                                            </button>
                                                                    )
                                                                }
                                                            </td>
                                                            <td>
                                                                {enrollment.firstName} {enrollment.lastName}
                                                                {
                                                                    /* Antes esto se decia pintando la fila entera de rosa. Una
                                                                       etiqueta al lado del nombre se lee; un fondo de color, no. */
                                                                    enrollment.generalBalance?.expiredMonthlyBalance?.value > 0
                                                                        ? <span className="waDebe" title="Mensualidad vencida: mandar a Caja">Debe</span>
                                                                        : null
                                                                }
                                                            </td>
                                                            <td
                                                                style={{ color: `${enrollment.enrollmentStatusId == ENROLLMENT_STATUS.ACTIVE ? '#00e669' : ''}` }}
                                                            >
                                                                {enrollment.enrollmentStatus}
                                                            </td>
                                                            <td className="menotrStylesMobileItem">
                                                                {enrollment.correlative}
                                                            </td>
                                                            <td className="menotrStylesMobileItem">
                                                                {enrollment.enrollmentUpdatedFullDate}
                                                            </td>
                                                            <td className="menotrStylesMobileItem">
                                                                <div className="d-flex justify-content-center">
                                                                    <div className="textMobile">
                                                                        {editableAttendance[enrollment.enrollmentId].attendanceSummary.attendedSessions}
                                                                    </div>
                                                                    <div className="textMobile px-1">
                                                                        /
                                                                    </div>
                                                                    <div className="textMobile">
                                                                        {editableAttendance[enrollment.enrollmentId].attendanceSummary.totalSessions}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="text-end menotrStylesMobileItem" >
                                                                <div className="d-flex">
                                                                    <div className="textMobile flex-grow-1">
                                                                        { editableAttendance[enrollment.enrollmentId].attendanceSummary.attendancePercentage }
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                            </tbody>
                                        </table>
                                        {
                                            !_.isEmpty(extraStudentsList) ?
                                                <div>
                                                    <h6 className="my-4 ms-2" style={{ color: "var(--wa-apagado)" }}>Estudiantes Visitantes</h6>
                                                    <table className="specialTable attendanceTable">
                                                        <thead>
                                                            <tr>
                                                                <th className="pb-2 text-align-center">
                                                                    {Constants.Common.identificationCode}
                                                                </th>
                                                                <th className="pb-2 text-align-center">
                                                                    Nombre
                                                                </th>
                                                                <th className="pb-2 text-align-center">
                                                                    Telefono
                                                                </th>
                                                                <th className="pb-2 text-align-center">
                                                                    Correlativo
                                                                </th>
                                                                <th className="pb-2 text-align-center">
                                                                    Notas
                                                                </th>
                                                                <th className="pb-2 text-align-center">
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {
                                                                Object.values(extraStudentsList).map((extraStudent, index: number) =>
                                                                    <tr key={"eS_" + index}>
                                                                        <td>
                                                                            <p className="cellMobileStyles" style={{ fontSize: "14px", margin: 0 }}>
                                                                                {extraStudent.identificationType}-{extraStudent.identificationCode}
                                                                            </p>
                                                                        </td>
                                                                        <td style={{ fontSize: "14px", margin: 0 }}>
                                                                            {`${extraStudent.firstName ? extraStudent.firstName : ""} ${extraStudent.lastName ? extraStudent.lastName : ""}`}
                                                                        </td>
                                                                        <td style={{ fontSize: "14px", margin: 0 }}>
                                                                            {extraStudent.phone}
                                                                        </td>
                                                                        <td style={{ fontSize: "14px", margin: 0 }}>
                                                                            {extraStudent.correlative}
                                                                        </td>
                                                                        <td style={{ fontSize: "14px", margin: 0 }}>
                                                                            {extraStudent.observations}
                                                                        </td>
                                                                        <td className="border-bottom-0">
                                                                            {
                                                                                !extraStudent.classId ?
                                                                                    <button type="button"
                                                                                        onClick={() => {
                                                                                            if (extraStudentsList[extraStudent.temporalId]) {
                                                                                                let newExtraStudentsList = extraStudentsList;
                                                                                                delete newExtraStudentsList[extraStudent.temporalId];
                                                                                                setExtraStudentsList({ ...newExtraStudentsList });
                                                                                            }
                                                                                        }}
                                                                                        disabled={idleLocked}
                                                                                        className="btn btn-outline-danger"
                                                                                        style={{
                                                                                            borderRadius: "50%",
                                                                                            display: "flex",
                                                                                            alignItems: "center",
                                                                                            justifyContent: "center",
                                                                                            fontSize: "10px",
                                                                                            height: "20px",
                                                                                            width: "20px",
                                                                                            padding: 0
                                                                                        }} >
                                                                                        <FontAwesomeIcon icon={solid("minus")} />
                                                                                    </button>
                                                                                    :
                                                                                    ""
                                                                            }
                                                                        </td>
                                                                    </tr>
                                                                )
                                                            }
                                                        </tbody>
                                                    </table>
                                                </div>
                                                :
                                                ""
                                        }
                                    </div>
                            }
                        </Tab>
                        <Tab
                            className="historic-tab"
                            eventKey="historic"
                            title="Histórico">
                            {
                                historicAttendances.length ?
                                    <div>
                                        {
                                            totalSesiones <= 4 ? null : <div className="waVentana">
                                                <span className="waVentanaTitulo">Ver</span>
                                                {
                                                    [4, 8, 16].map(cuantas =>
                                                        <button
                                                            key={"V_" + cuantas}
                                                            type="button"
                                                            className={ventanaSesiones === cuantas ? "waVentanaChip activo" : "waVentanaChip"}
                                                            onClick={() => setVentanaSesiones(cuantas)}
                                                        >
                                                            {"Últimas " + cuantas}
                                                        </button>
                                                    )
                                                }
                                                <button
                                                    type="button"
                                                    className={ventanaSesiones >= totalSesiones ? "waVentanaChip activo" : "waVentanaChip"}
                                                    onClick={() => setVentanaSesiones(totalSesiones)}
                                                >
                                                    {"Todo el periodo (" + totalSesiones + ")"}
                                                </button>
                                            </div>
                                        }
                                        {!esTelefono ? null : <div className="waHist">
                                            <div className="waHistModos">
                                                <button type="button"
                                                    className={modoHistorico === "dia" ? "waHistModo activo" : "waHistModo"}
                                                    onClick={() => setModoHistorico("dia")}
                                                >
                                                    Por día
                                                </button>
                                                <button type="button"
                                                    className={modoHistorico === "alumno" ? "waHistModo activo" : "waHistModo"}
                                                    onClick={() => setModoHistorico("alumno")}
                                                >
                                                    Por alumno
                                                </button>
                                            </div>

                                            {
                                                modoHistorico !== "dia" ? null : <>
                                                    <div className="waHistElige">
                                                        {
                                                            sesionesVisibles.map((session: any, i: number) =>
                                                                <button key={"D_" + i} type="button"
                                                                    className={(diaElegido ?? (sesionesVisibles.length - 1)) === i ? "waHistChip activo" : "waHistChip"}
                                                                    onClick={() => setDiaElegido(i)}
                                                                >
                                                                    {session.sessionOn}
                                                                </button>
                                                            )
                                                        }
                                                    </div>
                                                    {
                                                        sesionesVisibles.length < 1 ? null : (() =>
                                                        {
                                                            const i = diaElegido ?? (sesionesVisibles.length - 1);
                                                            const enEseDia = historicoOrdenado.map((e: any) =>
                                                            ({
                                                                nombre : e.fullName,
                                                                activo : e.enrollmentStatusId === ENROLLMENT_STATUS.ACTIVE,
                                                                dia    : e.attendanceByDay.slice(desdeSesion)[i]
                                                            }));
                                                            const vinieron = enEseDia.filter((x: any) => x.dia?.attendanceId && x.dia?.isPresent).length;
                                                            const faltaron = enEseDia.filter((x: any) => x.dia?.attendanceId && !x.dia?.isPresent).length;

                                                            return <>
                                                                <p className="waHistResumen">
                                                                    <b>{vinieron}</b> presentes · <b>{faltaron}</b> ausentes
                                                                </p>
                                                                <div className="waLt">{
                                                                    enEseDia.map((x: any, j: number) =>
                                                                        <article className={x.activo ? "waLtTarjeta" : "waLtTarjeta apagada"} key={"HD_" + j}>
                                                                            <header className="waLtCabecera">
                                                                                <span className="waLtTitulo">{x.nombre}</span>
                                                                                <span className={"waLtChip " + (!x.dia?.attendanceId ? "gris" : (x.dia.isPresent ? "verde" : "ambar"))}>
                                                                                    {!x.dia?.attendanceId ? "Sin registrar" : (x.dia.isPresent ? "Presente" : "Ausente")}
                                                                                </span>
                                                                            </header>
                                                                        </article>
                                                                    )
                                                                }</div>
                                                            </>
                                                        })()
                                                    }
                                                </>
                                            }

                                            {
                                                modoHistorico !== "alumno" ? null : <>
                                                    <div className="waHistElige">
                                                        {
                                                            historicoOrdenado.map((e: any) =>
                                                                <button key={"A_" + e.enrollmentId} type="button"
                                                                    className={(alumnoElegido ?? historicoOrdenado[0]?.enrollmentId) === e.enrollmentId ? "waHistChip activo" : "waHistChip"}
                                                                    onClick={() => setAlumnoElegido(e.enrollmentId)}
                                                                >
                                                                    {e.fullName}
                                                                </button>
                                                            )
                                                        }
                                                    </div>
                                                    {
                                                        historicoOrdenado.length < 1 ? null : (() =>
                                                        {
                                                            const id = alumnoElegido ?? historicoOrdenado[0].enrollmentId;
                                                            const alumno = historicoOrdenado.find((e: any) => e.enrollmentId === id) || historicoOrdenado[0];
                                                            const dias = alumno.attendanceByDay.slice(desdeSesion);
                                                            const registrados = dias.filter((d: any) => d.attendanceId).length;
                                                            const presentes = dias.filter((d: any) => d.attendanceId && d.isPresent).length;
                                                            const porcentaje = registrados < 1 ? 0 : Math.round((presentes / registrados) * 100);

                                                            return <>
                                                                <p className="waHistResumen">
                                                                    <b>{presentes}</b> de <b>{registrados}</b> sesiones registradas · <b>{porcentaje}%</b>
                                                                </p>
                                                                <div className="waLt">{
                                                                    dias.map((d: any, j: number) =>
                                                                        <article className="waLtTarjeta" key={"HA_" + j}>
                                                                            <header className="waLtCabecera">
                                                                                <span className="waLtTitulo">{sesionesVisibles[j]?.sessionOn}</span>
                                                                                <span className={"waLtChip " + (!d.attendanceId ? "gris" : (d.isPresent ? "verde" : "ambar"))}>
                                                                                    {!d.attendanceId ? "Sin registrar" : (d.isPresent ? "Presente" : "Ausente")}
                                                                                </span>
                                                                            </header>
                                                                        </article>
                                                                    )
                                                                }</div>
                                                            </>
                                                        })()
                                                    }
                                                </>
                                            }
                                        </div>}
                                        <table className="specialTable attendanceTable" style={{display : esTelefono ? "none" : "table"}}>
                                            <thead>
                                                <tr>
                                                    <th className="menotrStylesMobileItem d-none d-lg-table-cell">
                                                        Correlativo
                                                    </th>
                                                    <th className="sticky">
                                                        Nombre
                                                    </th>
                                                    <th className="d-none d-lg-table-cell">
                                                        Estatus Matrícula
                                                    </th>
                                                    <th className="menotrStylesMobileItem d-none d-lg-table-cell">
                                                        Última Actualización
                                                    </th>
                                                    {
                                                        sesionesOcultas === 0 ? null :
                                                            <th className="waPlegado">
                                                                <button type="button" onClick={() => setVentanaSesiones(totalSesiones)}>
                                                                    {"‹ " + sesionesOcultas + " antes"}
                                                                </button>
                                                            </th>
                                                    }
                                                    {
                                                        sesionesVisibles.map((session, index) => (
                                                            <th
                                                                key={index}
                                                                className="text-center px-2"
                                                                style={{ color: session.color }}
                                                            >
                                                                <div className="d-flex align-items-center justify-content-center">
                                                                    {
                                                                        session.typeId == ATTENDANCE_TYPE.COMPENSATION &&
                                                                        <OverlayTrigger
                                                                            placement="top"
                                                                            overlay={
                                                                                <Tooltip id="add-day-tooltip">
                                                                                    {Constants.Common.compensatingDay}
                                                                                </Tooltip>
                                                                            }
                                                                        >
                                                                            <FontAwesomeIcon style={{color: 'gray', marginRight: '5px'}} icon={solid("circle-info")} />
                                                                        </OverlayTrigger>
                                                                    }
                                                                    {
                                                                        session.statusId == ATTENDANCE_STATUS.CANCELLED &&
                                                                        <OverlayTrigger
                                                                            placement="top"
                                                                            overlay={
                                                                                <Tooltip id="add-day-tooltip">
                                                                                    {Constants.Common.canceledDay}
                                                                                </Tooltip>
                                                                            }
                                                                        >
                                                                            <FontAwesomeIcon style={{color: 'gray', marginRight: '5px'}} icon={solid("circle-info")} />
                                                                        </OverlayTrigger>
                                                                    }
                                                                    <span>{session.sessionOn}</span>
                                                                    <button
                                                                        className="btn btn-link edit-day-button ms-2"
                                                                        onClick={() => {
                                                                            detailDayProps.openDetailDayModal(session);
                                                                        }}
                                                                        disabled={idleLocked}
                                                                    >
                                                                        <FontAwesomeIcon icon={solid("ellipsis-vertical")} />
                                                                    </button>
                                                                </div>
                                                            </th>
                                                        ))
                                                    }
                                                    {userProfile.currentRole !== ROLE.MENTOR &&
                                                        <th className="text-center">
                                                            <OverlayTrigger
                                                                placement="top"
                                                                overlay={
                                                                    <Tooltip id="add-day-tooltip">
                                                                        {Constants.Common.addDay}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <button 
                                                                    className="btn btn-success rounded-circle d-block mx-auto" 
                                                                    style={{ 
                                                                        width: '30px', 
                                                                        height: '30px', 
                                                                        padding: '0',
                                                                        backgroundColor: '#28a745',
                                                                        borderColor: '#28a745'
                                                                    }}
                                                                    onClick={() => addDayProps.setShowAddDayModal(true)}
                                                                    disabled={idleLocked}
                                                                >
                                                                    <FontAwesomeIcon icon={solid("plus")} />
                                                                </button>
                                                            </OverlayTrigger>
                                                        </th>
                                                    }
                                                    <th className="text-center menotrStylesMobileItem">
                                                        Total a Fecha
                                                    </th>
                                                    <th className="text-center menotrStylesMobileItem">
                                                        %
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {
                                                    historicoOrdenado.map((enrollment) =>
                                                        <tr
                                                            key={"S_" + enrollment.enrollmentId}
                                                            className={addClasses("enrollment", enrollment)}>
                                                            <td className="menotrStylesMobileItem d-none d-lg-table-cell">
                                                                {enrollment.correlative}
                                                            </td>
                                                            <td className="sticky">
                                                                {enrollment.fullName}
                                                            </td>
                                                            <td
                                                                className={`d-none d-lg-table-cell ${enrollment.enrollmentStatusId == ENROLLMENT_STATUS.ACTIVE ? 'greenActive' : ''}`}>
                                                                {enrollment.enrollmentStatus}
                                                            </td>
                                                            <td className="menotrStylesMobileItem d-none d-lg-table-cell">
                                                                {enrollment.enrollmentUpdatedFullDate}
                                                            </td>
                                                            {
                                                                sesionesOcultas === 0 ? null : <td className="waPlegado" />
                                                            }
                                                            {
                                                                enrollment.attendanceByDay.slice(desdeSesion).map((day: any, index: number) =>
                                                                    <td
                                                                        key={index}
                                                                        className="text-center">
                                                                        {
                                                                            <div className="d-flex flex-column">
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-default d-flex flex-column align-items-center"
                                                                                    disabled={true}>
                                                                                    <FontAwesomeIcon
                                                                                        size="lg"
                                                                                        icon={
                                                                                            enrollment.enrollmentStatusId == ENROLLMENT_STATUS.ACTIVE ?
                                                                                                (
                                                                                                    day.sessionId && day.attendanceId ?
                                                                                                        (
                                                                                                            day.isPresent ?
                                                                                                                regular("square-check")
                                                                                                                :
                                                                                                                regular("square-minus")
                                                                                                        )
                                                                                                        :
                                                                                                        regular("square")
                                                                                                )
                                                                                                :
                                                                                                (
                                                                                                    day.sessionId && day.attendanceId ?
                                                                                                        (
                                                                                                            day.isPresent ?
                                                                                                                regular("square-check")
                                                                                                                :
                                                                                                                regular("square-minus")
                                                                                                        )
                                                                                                        :
                                                                                                        solid("square")
                                                                                                )
                                                                                        } style={{
                                                                                            color: (
                                                                                                day.sessionId && day.attendanceId ?
                                                                                                    (
                                                                                                        enrollment.enrollmentStatusId == ENROLLMENT_STATUS.ACTIVE ?
                                                                                                            (
                                                                                                                day.isPresent ?
                                                                                                                    "#00e669"
                                                                                                                    :
                                                                                                                    "#ec5353")
                                                                                                            :
                                                                                                            (
                                                                                                                day.isPresent ?
                                                                                                                    "#00e66980"
                                                                                                                    :
                                                                                                                    "#ec535380"
                                                                                                            )
                                                                                                    )
                                                                                                    :
                                                                                                    styles.modal.body.noActive.icon.v.color
                                                                                            )
                                                                                        }}
                                                                                    />
                                                                                    <span
                                                                                        className="modal_header_info_card_span_v"
                                                                                        style={{
                                                                                            fontSize: "10px",
                                                                                            color: (
                                                                                                day.sessionId && day.attendanceId
                                                                                                    ?
                                                                                                    (
                                                                                                        day.isPresent ?
                                                                                                            "#00e669"
                                                                                                            :
                                                                                                            "#ec5353"
                                                                                                    )
                                                                                                    :
                                                                                                    styles.modal.body.noActive.icon.v.color
                                                                                            )
                                                                                        }}>
                                                                                        {
                                                                                            day.sessionId && day.attendanceId
                                                                                                ?
                                                                                                (
                                                                                                    day.isPresent ?
                                                                                                        "Presente"
                                                                                                        :
                                                                                                        "Ausente"
                                                                                                )
                                                                                                :
                                                                                                "N/A"
                                                                                        }
                                                                                    </span>
                                                                                </button>
                                                                                {
                                                                                    day.notes ?
                                                                                        <button
                                                                                            onClick={() => { showNoteModalLecture(enrollment, day.notes) }}
                                                                                            disabled={idleLocked}
                                                                                            style={{
                                                                                                ...styles.modal.body.addNote.v,
                                                                                                color: "var(--wa-acento)"
                                                                                            }}
                                                                                        >
                                                                                            <span style={styles.modal.body.addNote.span.v}>
                                                                                                Nota
                                                                                            </span>
                                                                                        </button>
                                                                                        :
                                                                                        ""
                                                                                }
                                                                            </div>
                                                                        }
                                                                    </td>
                                                                )
                                                            }
                                                            {userProfile.currentRole !== ROLE.MENTOR &&
                                                                <td></td>
                                                            }
                                                            <td className="menotrStylesMobileItem">
                                                                <div className="d-flex justify-content-center">
                                                                    <div className="textMobile">
                                                                        {enrollment.attendanceSummary.attendedSessions}
                                                                    </div>
                                                                    <div className="textMobile px-1">
                                                                        /
                                                                    </div>
                                                                    <div className="textMobile">
                                                                        {enrollment.attendanceSummary.totalSessions}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="text-end menotrStylesMobileItem" >
                                                                <div className="d-flex">
                                                                    <div className="textMobile flex-grow-1">
                                                                        {enrollment.attendanceSummary.attendancePercentage}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                <tr className="sessionExtraStudentCount">
                                                    <td className="bg-white sticky">Estudiantes Visitantes</td>
                                                    <td className="d-none d-lg-table-cell"></td>
                                                    <td className="d-none d-lg-table-cell"></td>
                                                    <td className="d-none d-lg-table-cell"></td>
                                                    {
                                                        sessions.map((session, index) =>
                                                            <td key={index} className="text-center px-2 studentCount">
                                                                <button
                                                                    className={session.extraStudentsCount < 1 ? "noExtraStuden" : ""}
                                                                    disabled={idleLocked}
                                                                    onClick={session.extraStudentsCount < 1 ? () => { } : () => { openExtraStudentModalLecture(session) }}>
                                                                    <FontAwesomeIcon icon={solid('person-walking-luggage')} />
                                                                    <span>{session.extraStudentsCount}</span>
                                                                </button>
                                                            </td>
                                                        )
                                                    }
                                                </tr>
                                                <tr className="sessionDocuments">
                                                    <td className="bg-white sticky">Documentos</td>
                                                    <td className="d-none d-lg-table-cell"></td>
                                                    <td className="d-none d-lg-table-cell"></td>
                                                    <td className="d-none d-lg-table-cell"></td>
                                                    {
                                                        sessions.map((session, index) =>
                                                            session.documents
                                                                ?
                                                                <td key={index} className="text-center px-2">
                                                                    <div className="d-flex flex-row-reverse">
                                                                        {
                                                                            session.documents.map((document) =>
                                                                                <button
                                                                                    key={document.id}
                                                                                    className="me-3 btn btnOutlinePurple rounded-pill d-flex align-items-center fotter-btn-mobile"
                                                                                    type="button"
                                                                                    style={styles.modal.footer.img.v}
                                                                                    disabled={idleLocked}
                                                                                    onClick={() => downloadFile(document)}
                                                                                >
                                                                                    <FontAwesomeIcon className="fotter-btn-icon-mobile" icon={regular("circle-down")} />
                                                                                </button>
                                                                            )
                                                                        }
                                                                    </div>
                                                                </td>
                                                                :
                                                                ""
                                                        )
                                                    }
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    :
                                    <>
                                        <span>No hay información</span>
                                        <img width={120} src={noInfo} alt="no hay informacion" />
                                    </>
                            }
                        </Tab>

                        {(permissions.includes(PERMISSIONS_CODES.ATTENDANCE_PERMISSIONS.DELETE)
                            || userProfile.currentRole === ROLE.SUPER_ADMIN)
                            && (
                                <Tab
                                    className="historic-tab"
                                    eventKey="danger"
                                    title="Configuración Avanzada">
                                    {
                                        <div className="container">
                                            {
                                                sessions.length > 0 ?
                                                    <Button
                                                        disabled={idleLocked}
                                                        onClick={deleteSessions}
                                                        variant="danger">
                                                        {Constants.attendances.delete}
                                                    </Button>
                                                    : <Button
                                                        disabled={idleLocked}
                                                        onClick={createSessions}
                                                        variant="primary">
                                                        {Constants.schedules.generateSessions}
                                                    </Button>
                                            }
                                        </div>
                                    }
                                </Tab>
                            )
                        }
                    </Tabs>
                }
                footerContent={
                    (activeTab === 'historic' || (!selectedSession || !selectedAttendance || (userProfile.currentRole === ROLE.MENTOR && !selectedSession.isOnCurrentDay) || (userProfile.currentRole === ROLE.MENTOR && selectedSession.timeStatusId != TIME_STATUS.ON_TIME)))
                        ?
                        ""
                        :
                        <div className="w-100">
                            <div className="d-flex justify-content-between align-items-end pt-4 footer-content">
                                <button
                                    disabled={idleLocked}
                                    className="fotter-btn-mobile"
                                    style={idleLocked ? styles.modal.footer.extraStudent.disabled.v : styles.modal.footer.extraStudent.v}
                                    onClick={openExtraStudentModal}>
                                    <span className="d-none d-lg-flex pe-2">Estudiante Extra</span>
                                    <FontAwesomeIcon className="fotter-btn-icon-mobile" style={{ color: "rgb(143, 145, 218)", fontSize: "20px", marginBottom: "-1px" }} icon={solid("plus")} />
                                </button>
                                <div className="d-inline-grid mx-1">
                                    <div className="d-flex align-items-end end-buttons">
                                        {
                                            downloadDocuments
                                                ?
                                                <div className="d-flex flex-row-reverse">
                                                    {
                                                        downloadDocuments.map((document) =>
                                                            <button
                                                                key={document.id}
                                                                className="me-3 btn btnOutlinePurple rounded-pill d-flex align-items-center fotter-btn-mobile"
                                                                type="button"
                                                                style={styles.modal.footer.img.v}
                                                                disabled={idleLocked}
                                                                onClick={() => downloadFile(document)}
                                                            >
                                                                <FontAwesomeIcon className="fotter-btn-icon-mobile" icon={regular("circle-down")} />
                                                                <span className="d-none d-lg-flex ps-2">{document.name.length <= 20 ? document.name : document.name.slice(0, 20) + '...'}</span>
                                                            </button>
                                                        )
                                                    }
                                                </div>
                                                :
                                                ""
                                        }
                                        <FileUploadComponent
                                            allowedFiletypes={allowedFiletypes}
                                            onFilesChange={fileChange}
                                            contentStyle={styles.modal.footer.FileUpload.content.v}
                                            buttonStyle={styles.modal.footer.FileUpload.button.v}
                                            buttonClass={'fotter-btn-mobile'}
                                            listStyle={styles.modal.footer.FileUpload.list.v}
                                        />
                                        <button
                                            style={styles.modal.footer.save.v}
                                            onClick={() => { checkPoint() }}
                                            type="button"
                                            id="mainAction"
                                            className="rounded-pill btn btnPurple fotter-btn-mobile"
                                            disabled={
                                                idleLocked || !hasChanges(isNewAttendance).status
                                            }
                                        >
                                            <span className="d-flex">
                                                <FontAwesomeIcon className="fotter-btn-icon-mobile" icon={solid("check-circle")} />
                                            </span>
                                            <span className="d-none d-lg-flex ps-2">
                                                Guardar
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                }
            />
            <MiniModalController
                title="Notas"
                show={isShowNoteModal}
                onClose={closeNoteModal}
            >
                <ChatComponentController
                    chats={selectedNote}
                    onChange={changeNote}
                    lecture={isLecture}
                />
            </MiniModalController>
            <MiniModalController
                title="Estudiantes Visitantes"
                show={showExtraStudentModalLecture}
                onClose={closeExtraStudentModalLecture}
            >
                {
                    !_.isEmpty(extraStudentsListLecture) ?
                        <div className="pt-3">
                            <table className="specialTable attendanceTable extraStudentTable">
                                <thead>
                                    <tr>
                                        <th className="pb-2 text-align-center">
                                            {Constants.Common.identificationCode}
                                        </th>
                                        <th className="pb-2 text-align-center">
                                            Nombre
                                        </th>
                                        <th className="pb-2 text-align-center">
                                            Telefono
                                        </th>
                                        <th className="pb-2 text-align-center">
                                            Correlativo
                                        </th>
                                        <th className="pb-2 text-align-center">
                                            Notas
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        Object.values(extraStudentsListLecture).map((extraStudent, index: number) =>
                                            <tr key={"eS_" + index}>
                                                <td>
                                                    <p>
                                                        {extraStudent.identificationType}-{extraStudent.identificationCode}
                                                    </p>
                                                </td>
                                                <td>
                                                    {`${extraStudent.firstName ? extraStudent.firstName : ""} ${extraStudent.lastName ? extraStudent.lastName : ""}`}
                                                </td>
                                                <td>
                                                    {extraStudent.phone}
                                                </td>
                                                <td>
                                                    {extraStudent.correlative}
                                                </td>
                                                <td>
                                                    {extraStudent.observations}
                                                </td>
                                                <td className="border-bottom-0">
                                                </td>
                                            </tr>
                                        )
                                    }
                                </tbody>
                            </table>
                        </div>
                        :
                        ""
                }
            </MiniModalController>
            <MiniModalController
                title={Constants.Common.addDay}
                show={addDayProps.showAddDayModal}
                onClose={() => addDayProps.setShowAddDayModal(false)}
            >
                <addDayProps.AddDayForm />
            </MiniModalController>
            <MiniModalController
                title={Constants.Common.cancelDay}
                show={editDayProps.showEditDayModal}
                onClose={() => editDayProps.setShowEditDayModal(false)}
            >
                <editDayProps.EditDayForm />
            </MiniModalController>
            <MiniModalController
                title={Constants.Common.details}
                show={detailDayProps.showDetailDayModal}
                onClose={detailDayProps.closeDetailDayModal}
                >
                {detailDayProps.selectedDetailDay && (
                    <div className="d-flex flex-column">
                        <span><strong>Día:</strong> {detailDayProps.selectedDetailDay.sessionOn}</span>
                        <span style={{color: detailDayProps.selectedDetailDay.color}}><strong>Estatus:</strong> {detailDayProps.selectedDetailDay.status}</span>
                        <span><strong>Estatus de Tiempo:</strong> {detailDayProps.selectedDetailDay.timeStatus}</span>
                        <span><strong>Tipo de sesión:</strong> {detailDayProps.selectedDetailDay.type}</span>
                        <span><strong>Edificio:</strong> {detailDayProps.selectedDetailDay.building}</span>
                        <span><strong>Aula:</strong> {detailDayProps.selectedDetailDay.classroom}</span>
                        {detailDayProps.selectedDetailDay.notes && 
                            <span><strong>Descripción:</strong> {detailDayProps.selectedDetailDay.notes}</span>
                        }
                        {
                            permissions.includes(PERMISSIONS_CODES.ATTENDANCE_PERMISSIONS.DELETE)
                            && detailDayProps.selectedDetailDay.statusId != ATTENDANCE_STATUS.CANCELLED &&
                            <div className="text-center mt-3">
                                <button
                                    className="btn btn-danger"
                                    onClick={() => {
                                        detailDayProps.openCancellationOptionsModal(detailDayProps.selectedDetailDay);
                                        detailDayProps.closeDetailDayModal();
                                    }}
                                >
                                    <FontAwesomeIcon className="me-2" icon={solid("cancel")} />
                                    {Constants.Common.cancel}
                                </button>
                            </div>
                        }
                    </div>
                )}
            </MiniModalController>
            <MiniModalController
                title={Constants.Common.cancelOptions}
                show={cancellationOptionsProps.showCancellationOptionsModal}
                onClose={cancellationOptionsProps.closeCancellationOptionsModal}
            >
                <div className="d-flex flex-column align-items-center py-3">
                    <h5 className="mb-4">{Constants.Common.cancelConfirmOptions}</h5>
                    <div className="d-flex justify-content-center gap-3">
                        <Button 
                            variant="outline-danger"
                            onClick={() => cancellationOptionsProps.selectCancellationOption(false)}
                            disabled={idleLocked}
                        >
                            <FontAwesomeIcon className="me-2" icon={solid("ban")} />
                            {Constants.Common.cancelConfirmMessageReplacementButtonUndo}
                        </Button>
                        <Button
                            variant="outline-primary"
                            onClick={() => cancellationOptionsProps.selectCancellationOption(true)}
                            disabled={idleLocked}
                        >
                            <FontAwesomeIcon className="me-2" icon={solid("calendar-plus")} />
                            {Constants.Common.cancelConfirmMessageReplacementButton}
                        </Button>
                    </div>
                </div>
            </MiniModalController>
            <MiniModalController
                title={Constants.Common.cancelConfirm}
                show={cancellationConfirmProps.showCancellationConfirmModal}
                onClose={cancellationConfirmProps.cancelCancellation}
            >
                <div className="d-flex flex-column align-items-center py-3">
                    <h5 className="mb-4">{
                        cancellationOptionsProps.createReplacementSession
                        ? Constants.Common.cancelConfirmMessageReplacement
                        : Constants.Common.cancelConfirmMessage}
                    </h5>
                    <p className="text-center">{Constants.Common.cancelConfirmMessageUndo}</p>
                    <div className="d-flex justify-content-center gap-3 mt-3">
                        <Button 
                            variant="secondary"
                            onClick={cancellationConfirmProps.cancelCancellation}
                            disabled={idleLocked}
                        >
                            {Constants.Common.undo}
                        </Button>
                        <Button
                            variant="danger"
                            onClick={() => cancellationConfirmProps.executeCancellation()}
                            disabled={idleLocked}
                        >
                            {Constants.Common.cancelConfirm}
                        </Button>
                    </div>
                </div>
            </MiniModalController>
            <ExtraStudentModalCrontoller
                extraStudentsList={extraStudentsList}
                setExtraStudentsList={setExtraStudentsList}
                closeExtraStudentModal={closeExtraStudentModal}
                showExtraStudentModal={showExtraStudentModal}
            />
        </>
    )
}

export default AttendancesModalView